import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Terminal, Filter, Ban, ChevronLeft, ChevronRight, Eye } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/Card.js';
import { CommandStatusBadge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { SearchInput } from '../../components/ui/Input.js';
import { CopyButton } from '../../components/ui/CopyButton.js';
import { TableSkeleton } from '../../components/ui/Skeleton.js';
import { EmptyState, ErrorState } from '../../components/ui/EmptyState.js';
import { Modal, ConfirmDialog } from '../../components/ui/Modal.js';
import { JsonViewer } from '../../components/ui/JsonViewer.js';
import { PermissionGuard } from '../../components/security/PermissionGuard.js';
import { useToast } from '../../context/ToastContext.js';
import { useI18n } from '../../context/I18nContext.js';
import { fetchCommands, cancelCommand } from '../../api/commands.js';
import { formatDate, formatRelativeTime, truncateId } from '../../lib/utils.js';
import type { DeviceCommand } from '@hisob/types';

export function CommandsPage() {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [inspectCommand, setInspectCommand] = useState<DeviceCommand | null>(null);
  const [commandToCancel, setCommandToCancel] = useState<DeviceCommand | null>(null);

  const queryClient = useQueryClient();
  const { success, error: toastError } = useToast();
  const { t } = useI18n();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['admin-commands', { cursor, status: statusFilter }],
    queryFn: () => fetchCommands({ limit: 15, cursor, status: statusFilter !== 'all' ? statusFilter : undefined }),
    refetchInterval: 10000,
  });

  const cancelMutation = useMutation({
    mutationFn: (id: string) => cancelCommand(id),
    onSuccess: () => {
      success(t('commands.cancelSuccess'));
      setCommandToCancel(null);
      queryClient.invalidateQueries({ queryKey: ['admin-commands'] });
      queryClient.invalidateQueries({ queryKey: ['overview-metrics'] });
    },
    onError: (err: any) => {
      toastError(err.message || 'Failed to cancel command', err.requestId);
    },
  });

  const filteredCommands = (data?.items || []).filter((c) => {
    if (!search.trim()) return true;
    const term = search.toLowerCase();
    return (
      c.id.toLowerCase().includes(term) ||
      c.device_id.toLowerCase().includes(term) ||
      c.type.toLowerCase().includes(term) ||
      c.created_by.toLowerCase().includes(term)
    );
  });

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold tracking-tight text-slate-900 dark:text-slate-100 flex items-center gap-2">
            <Terminal className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
            {t('commands.title')}
          </h2>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
            {t('commands.description')}
          </p>
        </div>
      </div>

      {/* Filter Bar */}
      <Card>
        <CardContent className="p-4 flex flex-col sm:flex-row items-center gap-3">
          <div className="w-full sm:w-72">
            <SearchInput
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('commands.searchPlaceholder')}
            />
          </div>

          <div className="flex items-center gap-2 w-full sm:w-auto">
            <Filter className="w-4 h-4 text-slate-400" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="text-xs rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-slate-700 dark:text-slate-300 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="all">{t('commands.allStatuses')}</option>
              <option value="pending">Pending</option>
              <option value="acknowledged">Acknowledged</option>
              <option value="completed">Completed</option>
              <option value="failed">Failed</option>
              <option value="expired">Expired</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Commands Table */}
      <Card>
        <CardContent className="p-0 overflow-x-auto">
          {isLoading ? (
            <div className="p-6">
              <TableSkeleton rows={6} cols={6} />
            </div>
          ) : error ? (
            <div className="p-6">
              <ErrorState title={t('commands.loadError')} message={(error as any).message} onRetry={() => refetch()} />
            </div>
          ) : filteredCommands.length === 0 ? (
            <div className="p-6">
              <EmptyState
                title={t('commands.emptyTitle')}
                description={t('commands.emptyDesc')}
                actionText={t('common.resetFilters')}
                onAction={() => {
                  setSearch('');
                  setStatusFilter('all');
                }}
              />
            </div>
          ) : (
            <table className="w-full text-left text-xs">
              <thead className="bg-slate-50 dark:bg-slate-900/60 border-b border-slate-200 dark:border-slate-800 text-slate-500 font-semibold uppercase tracking-wider">
                <tr>
                  <th className="py-3 px-4">{t('commands.tableId')}</th>
                  <th className="py-3 px-4">{t('commands.tableDevice')}</th>
                  <th className="py-3 px-4">{t('commands.tableType')}</th>
                  <th className="py-3 px-4">{t('commands.tableStatus')}</th>
                  <th className="py-3 px-4">{t('commands.tableCreatedBy')}</th>
                  <th className="py-3 px-4">{t('commands.tableCreatedAt')}</th>
                  <th className="py-3 px-4">{t('commands.tableExpiresAt')}</th>
                  <th className="py-3 px-4 text-right">{t('commands.tableActions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800/80">
                {filteredCommands.map((cmd) => (
                  <tr key={cmd.id} className="hover:bg-slate-50 dark:hover:bg-slate-900/40 transition-colors">
                    <td className="py-3 px-4">
                      <CopyButton value={cmd.id} truncate={true} className="font-mono text-indigo-600 dark:text-indigo-400 font-medium" />
                    </td>
                    <td className="py-3 px-4">
                      <CopyButton value={cmd.device_id} truncate={true} />
                    </td>
                    <td className="py-3 px-4 font-mono font-semibold text-slate-800 dark:text-slate-200">
                      {t(`commandTypes.${cmd.type}`) !== `commandTypes.${cmd.type}` ? t(`commandTypes.${cmd.type}`) : cmd.type}
                    </td>
                    <td className="py-3 px-4">
                      <CommandStatusBadge status={cmd.status} />
                    </td>
                    <td className="py-3 px-4 text-slate-500 font-mono text-[11px]">
                      {truncateId(cmd.created_by, 6)}
                    </td>
                    <td className="py-3 px-4 text-slate-500">
                      {formatRelativeTime(cmd.created_at)}
                    </td>
                    <td className="py-3 px-4 text-slate-500">
                      {formatDate(cmd.expires_at)}
                    </td>
                    <td className="py-3 px-4 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          title="Inspect Payload & Timeline"
                          onClick={() => setInspectCommand(cmd)}
                        >
                          <Eye className="w-3.5 h-3.5" />
                        </Button>

                        {cmd.status === 'pending' && (
                          <PermissionGuard permission="commands:cancel">
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-rose-600 hover:bg-rose-50"
                              onClick={() => setCommandToCancel(cmd)}
                            >
                              <Ban className="w-3.5 h-3.5 mr-1" />
                              {t('commands.cancelCommandBtn')}
                            </Button>
                          </PermissionGuard>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>

        {/* Pagination */}
        {data && (
          <div className="flex items-center justify-between p-4 border-t border-slate-200 dark:border-slate-800 text-xs text-slate-500">
            <span>Showing {filteredCommands.length} commands</span>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                disabled={!cursor}
                onClick={() => setCursor(undefined)}
              >
                <ChevronLeft className="w-3.5 h-3.5 mr-1" /> {t('common.firstPage')}
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={!data.has_more || !data.next_cursor}
                onClick={() => setCursor(data.next_cursor || undefined)}
              >
                {t('common.nextPage')} <ChevronRight className="w-3.5 h-3.5 ml-1" />
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Inspect Modal */}
      {inspectCommand && (
        <Modal
          isOpen={Boolean(inspectCommand)}
          onClose={() => setInspectCommand(null)}
          title={t('commands.inspectModalTitle')}
          maxWidth="lg"
        >
          <div className="space-y-4 text-xs">
            <JsonViewer data={inspectCommand} />
          </div>
        </Modal>
      )}

      {/* Cancel Confirmation */}
      {commandToCancel && (
        <ConfirmDialog
          isOpen={Boolean(commandToCancel)}
          onClose={() => setCommandToCancel(null)}
          onConfirm={() => cancelMutation.mutate(commandToCancel.id)}
          title={t('commands.cancelConfirmTitle')}
          message={t('commands.cancelConfirmMessage')}
          confirmText={t('commands.cancelCommandBtn')}
          variant="danger"
          isLoading={cancelMutation.isPending}
        />
      )}
    </div>
  );
}
